import { useCallback, useEffect, useRef, useState } from 'react';
import Field from '../../components/common/Field';
import Spinner from '../../components/common/Spinner';
import { DONATION_METHODS, donationMethodOf } from '../../utils/constants';
import useClickOutside from '../../hooks/useClickOutside';
import { IconChevron } from '../../components/icons';

/* Method picker — a rich-select like the donor picker on Fundraising, so the
   open list can be styled: the design highlights the method currently chosen. */
function MethodPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, useCallback(() => setOpen(false), []));
  const current = donationMethodOf(value);
  return (
    <div className="rich-select" ref={ref}>
      <button type="button" className="rich-select-btn" onClick={() => setOpen((o) => !o)}>
        <span>{current.label}</span>
        <IconChevron />
      </button>
      <div className={`rich-select-menu ${open ? 'open' : ''}`}>
        {DONATION_METHODS.map((m) => (
          <div
            key={m.id}
            className={`rich-select-item ${m.id === value ? 'on' : ''}`}
            onClick={() => {
              onChange(m.id);
              setOpen(false);
            }}
          >
            {m.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Digits only, and a pasted full number keeps its last 4. Not maxLength={4}:
   that cuts "(512) 555-7006" to "(512" before the digits are picked out. */
const last4Of = (v) => String(v || '').replace(/\D/g, '').slice(-4);

/* Add/edit one way to pay: the method, and the one thing parents need for it
   (the field's label, placeholder and explanation come from the method), plus
   whose account it is — except "Other", which is just the handle or link.
   Venmo also asks for the last 4 digits of the account's phone. The
   recipient's name is optional. methodName is never sent, so an "Other"
   saved with a service name keeps it. */
export default function DonationMethodForm({ open, editing, saving, onClose, onSave }) {
  const [method, setMethod] = useState('zelle');
  const [handle, setHandle] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [last4, setLast4] = useState('');

  useEffect(() => {
    setMethod(editing?.method || 'zelle');
    setHandle(editing?.handle || '');
    setRecipientName(editing?.recipientName || '');
    setLast4(editing?.last4 || '');
  }, [editing, open]);

  const m = donationMethodOf(method);
  const needsRecipient = !!m.recipientHelp;
  const needsLast4 = !!m.last4Label;

  const save = () => {
    if (!handle.trim()) {
      window.alert(`Add the ${m.fieldLabel.toLowerCase()} parents should use.`);
      return;
    }
    if (needsLast4 && !last4) {
      window.alert('Add the last 4 digits of the phone on this Venmo account. Venmo asks parents for it the first time they pay.');
      return;
    }
    if (needsLast4 && !/^\d{4}$/.test(last4)) {
      window.alert('Just the last 4 digits of the phone number, like 0147.');
      return;
    }
    onSave({
      method,
      handle: handle.trim(),
      recipientName: needsRecipient ? recipientName.trim() : '',
      last4: needsLast4 ? last4 : '',
    });
  };

  return (
    <div className={`inline-form ${open ? 'on' : ''}`}>
      <div className="form-grid">
        <Field label="Method">
          <MethodPicker value={method} onChange={setMethod} />
        </Field>
        <Field label={m.fieldLabel} hint={m.help}>
          <input
            type="text"
            value={handle}
            maxLength={100}
            placeholder={m.placeholder}
            onChange={(e) => setHandle(e.target.value)}
          />
        </Field>
        {/* Venmo: the name and the last 4 share the second row */}
        {needsRecipient && (
          <Field
            label={m.recipientLabel || 'Recipient’s name'}
            hint={m.recipientHelp}
            style={needsLast4 ? undefined : { gridColumn: '1/-1' }}
          >
            <input
              type="text"
              value={recipientName}
              maxLength={100}
              placeholder="e.g. Tony Martin"
              onChange={(e) => setRecipientName(e.target.value)}
            />
          </Field>
        )}
        {needsLast4 && (
          <Field label={m.last4Label} hint={m.last4Help}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={last4}
              placeholder={m.last4Placeholder}
              onChange={(e) => setLast4(last4Of(e.target.value))}
            />
          </Field>
        )}
      </div>
      <div className="inline-form-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? <><Spinner /> Saving…</> : 'Save Method'}
        </button>
      </div>
    </div>
  );
}
